'use client';

/** 同页并发 claim（覆盖 React Strict Mode 双挂载） */
const inFlight = new Set<string>();

/** session：一次浏览会话内唯一即可；local：账号维度终身一次 */
type Persist = 'session' | 'local';

function hasReported(key: string, persist: Persist): boolean {
  try {
    const store = persist === 'local' ? localStorage : sessionStorage;
    return store.getItem(key) === '1';
  } catch {
    return false;
  }
}

function markReported(key: string, persist: Persist) {
  try {
    const store = persist === 'local' ? localStorage : sessionStorage;
    store.setItem(key, '1');
  } catch {
    // ignore
  }
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal?.aborted) {
      resolve();
      return;
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      resolve();
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/** 轮询等待第三方脚本把全局函数挂上 window */
function waitForReady(
  isReady: () => boolean,
  signal?: AbortSignal,
  maxAttempts = 25,
): Promise<boolean> {
  return new Promise((resolve) => {
    let attempts = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const cleanup = () => {
      if (timer !== undefined) clearTimeout(timer);
      signal?.removeEventListener('abort', onAbort);
    };

    const onAbort = () => {
      cleanup();
      resolve(false);
    };

    if (signal?.aborted) {
      resolve(false);
      return;
    }
    signal?.addEventListener('abort', onAbort, { once: true });

    const tick = () => {
      if (signal?.aborted) {
        cleanup();
        resolve(false);
        return;
      }
      if (isReady()) {
        cleanup();
        resolve(true);
        return;
      }
      attempts += 1;
      if (attempts >= maxAttempts) {
        cleanup();
        resolve(false);
        return;
      }
      timer = setTimeout(tick, 200);
    };
    tick();
  });
}

/**
 * 保证同一 key 的转化只上报一次。
 * - inFlight + storage 防并发/刷新重复
 * - 若上一次挂载仍 inFlight，等待其结束再决定是否补发（避免 Strict Mode 漏报）
 * - AbortSignal：卸载后停止等待与发送
 */
export async function reportConversionOnce(options: {
  key: string;
  isReady: () => boolean;
  send: () => void;
  signal?: AbortSignal;
  persist?: Persist;
}): Promise<void> {
  const { key, isReady, send, signal, persist = 'session' } = options;
  if (!key || hasReported(key, persist) || signal?.aborted) return;

  while (inFlight.has(key)) {
    if (signal?.aborted) return;
    await sleep(50, signal);
  }
  if (hasReported(key, persist) || signal?.aborted) return;

  inFlight.add(key);
  try {
    const ready = await waitForReady(isReady, signal);
    if (signal?.aborted || !ready) return;
    if (hasReported(key, persist)) return;

    markReported(key, persist);
    send();
  } finally {
    inFlight.delete(key);
  }
}
